import Router from 'vue-router';
import { CareType } from '@/types/care_type';
import { RouteQuery } from '@/helpers/utmQueryParams';
import { navigateToReturnPath, isInvalidReturnPath, getReturnPathFromQuery } from './routerNavigation';

const QUERY_PARAMS_EXCLUDED_FROM_FORWARD = new Set(['return_path', 'returnPath', 'skip_backup_care']);

export interface JobPostRedirectOptions {
  source?: string;
  reservationId?: string;
  return_path?: string;
  query?: RouteQuery;
}

/**
 * Drops navigation-control keys (return_path, skip_backup_care) so handoffs only forward
 * attribution params. Placing return_path on care-type lets navigateToReturnPath hijack
 * child-care job-post redirects and drop source/UTMs.
 */
export function omitExcludedForwardQueryParams(query: RouteQuery = {}): RouteQuery {
  return Object.fromEntries(Object.entries(query).filter(([key]) => !QUERY_PARAMS_EXCLUDED_FROM_FORWARD.has(key)));
}

/**
 * Determines if a care type is a child care type (babysitter, nanny, or child_care).
 * Child care types use the SPA route /job-post, while non-child care types use server-side routes.
 */
function isChildCareType(careType: string | undefined): boolean {
  if (!careType) return false;
  const childCareTypes = [CareType.BABYSITTER.toLowerCase(), CareType.NANNY.toLowerCase(), 'child_care'];
  return childCareTypes.includes(careType);
}

/**
 * Builds the redirect URL for non-child care types.
 * These care types use the server-side route /job_posting/new with a care_type query parameter.
 */
function buildNonChildCareRedirectUrl(careType: string | undefined): string {
  const careTypeToUrlMap: Record<string, string> = {
    [CareType.HOUSEKEEPING.toLowerCase()]: '/job_posting/new?care_type=housekeeping',
    [CareType.PET_CARE.toLowerCase()]: '/job_posting/new?care_type=pet_care',
    [CareType.SPECIAL_NEEDS.toLowerCase()]: '/job_posting/new?care_type=special_needs',
    [CareType.COMPANION_CARE.toLowerCase()]: '/job_posting/new?care_type=companion_care',
  };

  return careTypeToUrlMap[careType || ''] || '/';
}

function queryParamStringValue(value: RouteQuery[string]): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string') {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed || undefined;
}

/**
 * Merges additional query params onto a path without overwriting keys already present.
 * Skips return_path / skip_backup_care so caller-controlled redirect options stay authoritative.
 */
function appendForwardedQueryParams(baseUrl: string, query?: RouteQuery): string {
  if (!query) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.origin);

    Object.entries(query).forEach(([key, value]) => {
      if (QUERY_PARAMS_EXCLUDED_FROM_FORWARD.has(key) || url.searchParams.has(key)) {
        return;
      }

      const stringValue = queryParamStringValue(value);
      if (stringValue) {
        url.searchParams.set(key, stringValue);
      }
    });

    return url.pathname + url.search;
  } catch {
    return baseUrl;
  }
}

/**
 * Appends the return_path query parameter to a base URL if it exists and is valid.
 * Handles both URL construction and manual string concatenation as fallback.
 * When an explicit return_path is provided, it is used without validation to allow callers
 * to override the default validation rules.
 */
function appendReturnPathIfValid(baseUrl: string, router: Router, explicitReturnPath?: string): string {
  const returnPath = explicitReturnPath || getReturnPathFromQuery(router);

  if (!returnPath) {
    return baseUrl;
  }

  // Only validate return_path from router query, not explicit return_path from options
  if (!explicitReturnPath && isInvalidReturnPath(returnPath)) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('return_path', returnPath);
    return url.pathname + url.search;
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}return_path=${encodeURIComponent(returnPath)}`;
  }
}

/**
 * Redirects to the appropriate job posting route based on care type.
 *
 * For child care types (BABYSITTER, NANNY, CHILD_CARE):
 * - Uses the SPA route /job-post via router.push
 * - Supports optional query parameters like source and reservationId
 * - Handles return_path via navigateToReturnPath
 *
 * For non-child care types (HOUSEKEEPING, PET_CARE, SPECIAL_NEEDS, COMPANION_CARE):
 * - Uses the server-side route /job_posting/new?care_type=<type> via window.location.href
 * - Appends return_path query parameter if valid
 * - Preserves browser history so users can navigate back
 *
 * @param careType - The care type to redirect for (CareType enum or 'CHILD_CARE')
 * @param router - Vue Router instance for navigation
 * @param options - Optional parameters like source or reservationId
 */
export async function redirectToJobPost(
  careType: CareType | 'CHILD_CARE',
  router: Router,
  options?: JobPostRedirectOptions
): Promise<void> {
  const normalizedCareType = careType?.toLowerCase();
  const isChildCare = isChildCareType(normalizedCareType);

  if (isChildCare) {
    let defaultPath = '/job-post';

    if (options?.reservationId) {
      defaultPath = `/job-post?reservationId=${options.reservationId}`;
    } else if (options?.source) {
      defaultPath = `/job-post?source=${options.source}`;
    }

    navigateToReturnPath(router, appendForwardedQueryParams(defaultPath, options?.query));
    return;
  }

  const redirectUrl = buildNonChildCareRedirectUrl(normalizedCareType);
  const withForwardedQuery = appendForwardedQueryParams(redirectUrl, options?.query);
  const finalUrl = appendReturnPathIfValid(withForwardedQuery, router, options?.return_path);

  window.location.href = finalUrl;
}
